import React from 'react';
import { CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { ComplianceRequirement } from '../../types/compliance';
import { formatDate } from '../../utils/formatters';

interface ComplianceListProps {
  requirements: ComplianceRequirement[];
  onStatusChange: (requirementId: string) => void;
  onDelete: (requirementId: string) => void;
  onViewDetails: (requirementId: string) => void;
}

export default function ComplianceList({
  requirements,
  onStatusChange,
  onDelete,
  onViewDetails
}: ComplianceListProps) {
  const getStatusIcon = (status: string, dueDate: string) => {
    if (status === 'completed') {
      return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    }
    if (new Date(dueDate) < new Date()) {
      return <AlertTriangle className="w-5 h-5 text-red-500" />;
    }
    return <Clock className="w-5 h-5 text-yellow-500" />;
  };

  return (
    <div className="divide-y divide-gray-200">
      {requirements.map((requirement) => (
        <div key={requirement.id} className="p-6 hover:bg-gray-50">
          <div className="flex items-start justify-between">
            <div 
              className="flex items-start space-x-3 cursor-pointer"
              onClick={() => onViewDetails(requirement.id)}
            >
              {getStatusIcon(requirement.status, requirement.dueDate)}
              <div>
                <h4 className="text-sm font-medium text-gray-900">{requirement.title}</h4>
                <p className="mt-1 text-sm text-gray-500">{requirement.description}</p>
                <div className="mt-2 flex items-center space-x-4">
                  <span className="text-xs text-gray-500">
                    Due: {formatDate(requirement.dueDate)}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    requirement.priority === 'high'
                      ? 'bg-red-100 text-red-800'
                      : requirement.priority === 'medium'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {requirement.priority.toUpperCase()}
                  </span>
                  <span className="text-xs font-medium text-gray-500 capitalize">
                    {requirement.category}
                  </span>
                  {requirement.jurisdiction && (
                    <span className="text-xs text-gray-500">
                      {requirement.jurisdiction}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onStatusChange(requirement.id)}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                {requirement.status === 'completed' ? 'Reopen' : 'Mark Complete'}
              </button>
              <button
                onClick={() => onDelete(requirement.id)}
                className="text-sm text-red-600 hover:text-red-900"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}